import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { 
  Languages, 
  Sun, 
  Moon, 
  Bell, 
  BellOff, 
  Save, 
  AlertTriangle,
  Camera,
  User,
  Calendar,
  Clock,
  MapPin,
  Zap,
  CreditCard,
  Trash2,
  Loader2,
  LayoutDashboard,
  Eye,
  EyeOff,
  Menu
} from 'lucide-react';
import { useTheme } from '@/lib/hooks/useTheme';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNotifications } from '@/lib/hooks/useNotifications';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { deleteUserAccount } from '@/lib/db';
import { NotificationTest } from '@/components/updates/NotificationTest';
import { BetaAccessCenter } from '@/components/updates/BetaAccessCenter';
import { Switch } from '@/components/ui/switch';

interface PageVisibility {
  home: boolean;
  about: boolean;
  tools: boolean;
  consultations: boolean;
  resources: boolean;
  contact: boolean;
  forum: boolean;
  lectureDesign: boolean;
  parcours: boolean;
  tirageCartes: boolean;
}

interface MenuVisibility {
  mainNav: boolean;
  userMenu: boolean;
  footer: boolean;
  socialLinks: boolean;
  languageSelector: boolean;
  themeSelector: boolean;
}

interface ToolVisibility {
  barometre: boolean;
  projectorWatch: boolean;
  align: boolean;
}

export function AdminSettings() {
  const { toast } = useToast();
  const [theme, setTheme] = useTheme();
  const { language, setLanguage } = useLanguage();
  const { enabled: notificationsEnabled, toggleNotifications } = useNotifications();
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);

  // États pour les visibilités
  const [pageVisibility, setPageVisibility] = useState<PageVisibility>({
    home: true,
    about: true,
    tools: true,
    consultations: true,
    resources: true,
    contact: true,
    forum: true,
    lectureDesign: true,
    parcours: true,
    tirageCartes: true
  });

  const [menuVisibility, setMenuVisibility] = useState<MenuVisibility>({
    mainNav: true,
    userMenu: true,
    footer: true,
    socialLinks: true,
    languageSelector: true,
    themeSelector: true
  });

  const [toolVisibility, setToolVisibility] = useState<ToolVisibility>({
    barometre: true,
    projectorWatch: false,
    align: false
  });

  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Charger la visibilité des pages
        const { data: pageSettings, error: pageError } = await supabase
          .from('admin_settings')
          .select('value')
          .eq('key', 'page_visibility')
          .maybeSingle();

        if (pageError && pageError.code !== 'PGRST116') {
          console.error('Error loading page visibility:', pageError);
          return;
        }

        if (pageSettings?.value) {
          setPageVisibility(pageSettings.value);
        }

        // Charger la visibilité des menus
        const { data: menuSettings, error: menuError } = await supabase
          .from('admin_settings')
          .select('value')
          .eq('key', 'menu_visibility')
          .maybeSingle();

        if (menuError && menuError.code !== 'PGRST116') {
          console.error('Error loading menu visibility:', menuError);
          return;
        }

        if (menuSettings?.value) {
          setMenuVisibility(menuSettings.value);
        }

        // Charger la visibilité des outils
        const { data: toolSettings, error: toolError } = await supabase
          .from('admin_settings')
          .select('value')
          .eq('key', 'tool_visibility')
          .maybeSingle();

        if (toolError && toolError.code !== 'PGRST116') {
          console.error('Error loading tool visibility:', toolError);
          return;
        }

        if (toolSettings?.value) {
          setToolVisibility(toolSettings.value);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de charger les paramètres.",
        });
      }
    };

    loadSettings();
  }, [toast]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Sauvegarder la visibilité des pages
      const { error: pageError } = await supabase
        .from('admin_settings')
        .upsert({
          key: 'page_visibility',
          value: pageVisibility,
          description: 'Contrôle la visibilité des pages du site',
          category: 'visibility'
        }, {
          onConflict: 'key'
        });

      if (pageError) throw pageError;

      // Sauvegarder la visibilité des menus
      const { error: menuError } = await supabase
        .from('admin_settings')
        .upsert({
          key: 'menu_visibility',
          value: menuVisibility,
          description: 'Contrôle la visibilité des éléments de navigation',
          category: 'visibility'
        }, {
          onConflict: 'key'
        });

      if (menuError) throw menuError;

      // Sauvegarder la visibilité des outils
      const { error: toolError } = await supabase
        .from('admin_settings')
        .upsert({
          key: 'tool_visibility',
          value: toolVisibility,
          description: 'Contrôle la visibilité des outils HED',
          category: 'visibility'
        }, {
          onConflict: 'key'
        });

      if (toolError) throw toolError;

      toast({
        title: "Paramètres sauvegardés",
        description: "Les modifications ont été enregistrées avec succès.",
      });

      // Recharger la page pour appliquer les changements
      window.location.reload();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de sauvegarder les paramètres.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Visibilité des Pages */}
      <Card>
        <CardHeader>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <LayoutDashboard className="w-5 h-5 text-blue-600" />
            Visibilité des Pages
          </h2>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(pageVisibility).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between">
                <div>
                  <p className="font-medium capitalize">{key.replace(/([A-Z])/g, ' $1')}</p>
                  <p className="text-sm text-gray-500">
                    {value ? (
                      <span className="flex items-center gap-1 text-green-600">
                        <Eye className="w-4 h-4" /> Visible
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-red-600">
                        <EyeOff className="w-4 h-4" /> Masquée
                      </span>
                    )}
                  </p>
                </div>
                <Switch
                  checked={value}
                  onCheckedChange={(checked) => 
                    setPageVisibility(prev => ({ ...prev, [key]: checked }))
                  }
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Visibilité des Menus */}
      <Card>
        <CardHeader>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Menu className="w-5 h-5 text-blue-600" />
            Visibilité des Menus
          </h2>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(menuVisibility).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between">
                <div>
                  <p className="font-medium capitalize">{key.replace(/([A-Z])/g, ' $1')}</p>
                  <p className="text-sm text-gray-500">
                    {value ? (
                      <span className="flex items-center gap-1 text-green-600">
                        <Eye className="w-4 h-4" /> Visible
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-red-600">
                        <EyeOff className="w-4 h-4" /> Masqué
                      </span>
                    )}
                  </p>
                </div>
                <Switch
                  checked={value}
                  onCheckedChange={(checked) => 
                    setMenuVisibility(prev => ({ ...prev, [key]: checked }))
                  }
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Visibilité des Outils */}
      <Card>
        <CardHeader>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Zap className="w-5 h-5 text-blue-600" />
            Visibilité des Outils
          </h2>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(toolVisibility).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between">
                <div>
                  <p className="font-medium capitalize">{key}</p>
                  <p className="text-sm text-gray-500">
                    {value ? (
                      <span className="flex items-center gap-1 text-green-600">
                        <Eye className="w-4 h-4" /> Visible
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-red-600">
                        <EyeOff className="w-4 h-4" /> Masqué
                      </span>
                    )}
                  </p>
                </div>
                <Switch
                  checked={value}
                  onCheckedChange={(checked) => 
                    setToolVisibility(prev => ({ ...prev, [key]: checked }))
                  }
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Bouton de sauvegarde */}
      <Button
        onClick={handleSave}
        disabled={saving}
        className="w-full bg-[#54075B] hover:bg-[#54075B]/90 flex items-center justify-center gap-2"
      >
        {saving ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Sauvegarde...
          </>
        ) : (
          <>
            <Save className="w-4 h-4" />
            Sauvegarder les modifications
          </>
        )}
      </Button>
    </div>
  );
}